import {assetUrl, useStudio} from '../store';

/**
 * A permanent pipeline timeline — always visible at the top of the workspace.
 * Never disappears. Shows completed stages with a checkmark, active with a spinner,
 * and pending as numbered circles. Acts as a persistent progress indicator.
 */

export function PipelineStepper() {
  const s = useStudio();
  const project = s.project;
  if (!project) return null;

  const doc = project.document;
  const scenes = s.scenes();
  const currentJob = s.toast?.kind ?? '';

  // Compute stage statuses
  const hasConcept = !!doc.concept;
  const hasScenes = scenes.length > 0;
  const scenesWithVisual = scenes.filter((sc) => assetUrl(sc.visual)).length;
  const scenesWithVoice = scenes.filter((sc) => sc.voiceover?.s3_key).length;
  const allVisualsReady = hasScenes && scenesWithVisual === scenes.length;
  const someVisualsReady = hasScenes && scenesWithVisual > 0;
  const allVoiceReady = hasScenes && scenesWithVoice === scenes.length;
  const someVoiceReady = hasScenes && scenesWithVoice > 0;
  const missingVisuals = hasScenes ? scenes.length - scenesWithVisual : 0;

  type Status = 'done' | 'partial' | 'active' | 'pending' | 'needs-action';

  interface StageInfo {
    id: string;
    label: string;
    status: Status;
    detail?: string;
  }

  const stages: StageInfo[] = [
    {
      id: 'concept',
      label: 'الفكرة الإبداعية',
      status: hasConcept ? 'done' : currentJob === 'generate_concept' ? 'active' : 'pending',
    },
    {
      id: 'storyboard',
      label: 'المشاهد',
      status: hasScenes ? 'done' : currentJob === 'generate_script' ? 'active' : 'pending',
      detail: hasScenes ? `${scenes.length} مشاهد` : undefined,
    },
    {
      id: 'voiceover',
      label: 'التعليق الصوتي',
      status: allVoiceReady
        ? 'done'
        : currentJob === 'generate_voiceover'
          ? 'active'
          : someVoiceReady
            ? 'partial'
            : 'pending',
      detail: hasScenes && someVoiceReady ? `${scenesWithVoice}/${scenes.length}` : undefined,
    },
    {
      id: 'visuals',
      label: 'المرئيات',
      status: allVisualsReady
        ? 'done'
        : currentJob === 'match_archive' || currentJob === 'generate_visual'
          ? 'active'
          : someVisualsReady
            ? 'partial'
            : hasScenes && project.status === 'ready'
              ? 'needs-action'
              : 'pending',
      detail: hasScenes
        ? allVisualsReady
          ? `${scenesWithVisual} جاهز`
          : missingVisuals > 0 && project.status === 'ready'
            ? `${missingVisuals} بحاجة مرئيات`
            : undefined
        : undefined,
    },
  ];

  // Falls back to the stage's state in words when there is no count to show, so
  // every stage carries a readout rather than only the ones with numbers.
  const STATE_WORD: Record<Status, string> = {
    done: 'مكتملة',
    partial: 'جزئية',
    active: 'جارٍ العمل',
    'needs-action': 'تحتاج تدخّلك',
    pending: 'بانتظار',
  };

  return (
    <div className="pipeline-stepper">
      {stages.map((stage, i) => {
        // Each half-connector is coloured by the stage it reaches back to, so the
        // filled run ends exactly where progress ended.
        const reached = stage.status === 'done' || stage.status === 'partial';
        const before = i === 0 ? 'edge' : stages[i - 1]!.status === 'done' ? 'filled' : '';
        const after = i === stages.length - 1 ? 'edge' : reached ? 'filled' : '';

        return (
          <div key={stage.id} className={`pipeline-stage ${stage.status}`}>
            <div className="pipeline-track">
              <div className={`pipeline-line ${before}`} />
              <div className="pipeline-dot">
                {stage.status === 'done' && '✓'}
                {stage.status === 'partial' && '◐'}
                {stage.status === 'active' && <span className="spin-sm" />}
                {stage.status === 'needs-action' && '!'}
                {stage.status === 'pending' && i + 1}
              </div>
              <div className={`pipeline-line ${after}`} />
            </div>
            <span className="pipeline-label">{stage.label}</span>
            <span className="pipeline-detail">{stage.detail ?? STATE_WORD[stage.status]}</span>
          </div>
        );
      })}
    </div>
  );
}
